import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Turn an axios failure from `/users` into copy a shop owner can act on.
 *
 * The machine code lives at `response.data.cause.code` — NOT `data.code`, which
 * is the problem TYPE (`conflict`, `forbidden`, …) and, on the platform API, a
 * different shape entirely. Every guard the endpoints enforce is mapped here;
 * anything unmapped falls back to the server's own `detail` string, which is at
 * least specific, and finally to the generic server error.
 */
export function useUserError(): (err: unknown) => string {
  const { t } = useTranslation('users');
  const { t: tc } = useTranslation();

  return useCallback(
    (err: unknown): string => {
      const res = (
        err as { response?: { status?: number; data?: { detail?: string; cause?: { code?: string } } } }
      ).response;
      const code = res?.data?.cause?.code;
      const KEYS: Record<string, string> = {
        'users.email_taken': 'errors.emailTaken',
        'users.email_taken_globally': 'errors.emailTakenGlobally',
        'users.unknown_role': 'errors.unknownRole',
        'users.cannot_grant_unheld': 'errors.cannotGrantUnheld',
        'users.cannot_manage_superior': 'errors.cannotManageSuperior',
        'users.cannot_modify_self': 'errors.cannotModifySelf',
        'users.cannot_reset_self': 'errors.cannotResetSelf',
        'users.last_admin': 'errors.lastAdmin',
      };
      const key = code ? KEYS[code] : undefined;
      if (key) return t(key);
      if (res?.status === 404) return t('errors.notFound');
      // 429 has no `cause` — the shared rate limiter answers with a bare problem.
      if (res?.status === 429) return t('errors.tooMany');
      return res?.data?.detail ?? tc('errors:server');
    },
    [t, tc],
  );
}
